import React, { useState, useEffect, useMemo, useRef } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../common/NoRowsOverlay";
import * as TransactionService from "../../services/transaction.service";
import Swal from "sweetalert2";
import { handleServerValidations, formateDateWithMonth } from "../../../../utils/commonfunction";
import { SWAL_SETTINGS, THEME_COLORS } from "../../../../utils/Constants";
import { Placeholder, Tooltip, Whisper } from "rsuite";

ModuleRegistry.registerModules([AllCommunityModule]);

const TransactionsList = () => {
  const { t } = useTranslation();
  const gridRef = useRef();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clubTypeFilter, setClubTypeFilter] = useState("all");
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Transactions", url: "" },
  ];

  const onSelectionChanged = (event) => {
    if (event && event.api) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedRows(selectedData);
    }
  };

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["transactions-list", page, limit, search, statusFilter, clubTypeFilter],
    queryFn: () => TransactionService.getTransactionsList(page, limit, search, statusFilter, clubTypeFilter),
    refetchOnWindowFocus: false,
    retry: false,
    keepPreviousData: true,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const responseData = data?.data || {};
  const rowData = responseData.list || [];
  const totalCount = responseData.totalCount || 0;
  const totalPages = responseData.totalPages || 1;
  const currentPage = responseData.page || page;

  const handleDeleteSingle = (id) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        TransactionService.deleteTransaction(id)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message || "Record deleted successfully",
                ...SWAL_SETTINGS,
              });
              refetch();
              setSelectedRows([]);
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Are you sure you want to delete ${selectedRows.length} selected transaction(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const ids = selectedRows.map((row) => row.id);
          const response = await TransactionService.bulkDeleteTransactions({ ids });
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || `${selectedRows.length} record(s) deleted successfully`,
              ...SWAL_SETTINGS,
            });
            refetch();
            setSelectedRows([]);
            if (gridRef.current && gridRef.current.api) {
              gridRef.current.api.deselectAll();
            }
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        }
      }
    });
  };

  const colDefs = useMemo(() => [
    {
      headerName: "",
      field: "checkbox",
      maxWidth: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
    },
    { 
      headerName: "ID", 
      field: "id", 
      minWidth: 150, 
      maxWidth: 250, 
      tooltipField: "id",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    { headerName: "Club Name", field: "clubName", minWidth: 150 },
    { headerName: "Billing Period", field: "billingPeriod", minWidth: 130 },
    { 
      headerName: "Invoice ID", 
      field: "stripeInvoiceId", 
      minWidth: 150, 
      maxWidth: 200,
      tooltipField: "stripeInvoiceId",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    { headerName: "Club Type", field: "clubType", minWidth: 110, valueFormatter: params => params.value || "N/A" },
    { headerName: "Player Count", field: "playerCount", minWidth: 120 },
    { headerName: "Rate/Player", field: "rateApplied", valueFormatter: params => params.value !== undefined && params.value !== null ? `£${Number(params.value).toFixed(2)}` : "N/A", minWidth: 120 },
    { headerName: "Player Additions", field: "additionalPlayersCount", valueFormatter: params => params.value !== undefined ? params.value : 0, minWidth: 130 },
    { headerName: "Adjustment Amount", field: "additionalAmount", valueFormatter: params => `£${((params.value || 0) / 100).toFixed(2)}`, minWidth: 150 },
    { headerName: "Platform Fee", field: "platformFee", valueFormatter: params => `£${((params.value || 0) / 100).toFixed(2)}`, minWidth: 120 },
    { headerName: "Total Amount", field: "totalAmount", valueFormatter: params => `£${((params.value || 0) / 100).toFixed(2)}`, minWidth: 120 },
    { headerName: "Status", field: "status", minWidth: 120, cellRenderer: params => {
      const val = params.value ? params.value.toLowerCase() : "";
      let displayStatus = val;
      if (val === "flagged") {
        displayStatus = "paid";
      }
      
      let badgeClass = "badge-primary-light";
      if (displayStatus === "paid") {
        badgeClass = "badge-success-light";
      } else if (displayStatus === "failed") {
        badgeClass = "badge-danger-light";
      } else if (displayStatus === "invoiced") {
        badgeClass = "badge-info-light";
      } else if (displayStatus === "pending") {
        badgeClass = "badge-warning-light";
      }
      
      return <span className={`badge badge-pill ${badgeClass} fw-bold text-uppercase`}>{displayStatus || "N/A"}</span>;
    }},
    {
      headerName: "Created Date",
      field: "createdAt",
      minWidth: 180,
      valueFormatter: params => params.value ? formateDateWithMonth(params.value) : "N/A"
    },
    {
      headerName: t("list_heading_action"),
      minWidth: 100,
      maxWidth: 100,
      pinned: "right",
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
              >
                <button
                  type="button"
                  className="btn delete"
                  onClick={() => handleDeleteSingle(row.id)}
                >
                  <i className="ri-delete-bin-6-fill"></i>
                </button>
              </Whisper>
            </div>
          </div>
        );
      }
    }
  ], [t]);

  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    rowSelection: "multiple",
    onSelectionChanged: onSelectionChanged,
    getRowId: (params) => String(params.data.id),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: false,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
  }), [rowData, colDefs]);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleClubTypeChange = (e) => {
    setClubTypeFilter(e.target.value);
    setPage(1);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
              <div className="d-flex align-items-center gap-3">
                <h6 className="main-content-label mb-0">Transactions</h6>
                {selectedRows.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                    onClick={handleBulkDelete}
                  >
                    <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div style={{ width: 220 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Club Name, Invoice ID"
                    value={searchInput}
                    onChange={handleSearchChange}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>
                <div style={{ width: 150 }}>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={handleStatusChange}
                    style={{ outline: "none", boxShadow: "none" }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="invoiced">Invoiced</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div style={{ width: 150 }}>
                  <select
                    className="form-select"
                    value={clubTypeFilter}
                    onChange={handleClubTypeChange}
                    style={{ outline: "none", boxShadow: "none" }}
                  >
                    <option value="all">All Club Types</option>
                    <option value="Standard">Standard</option>
                    <option value="Partner">Partner</option>
                    <option value="Founder">Founder</option>
                  </select>
                </div>
              </div>
            </div>
            {isLoading ? (
              <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
            ) : (
              <>
                <div className="ag-theme-alpine" style={{ width: '100%' }}>
                  <AgGridReact {...getAgGridConfig} />
                </div>
                {rowData.length > 0 && (
                  <div
                    className="d-flex justify-content-between align-items-center mt-3 pt-2"
                    style={{
                      borderTop: "1px solid #dee2e6",
                      flexWrap: "wrap",
                      gap: "8px",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span>Page Size:</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={limit}
                        onChange={handleLimitChange}
                      >
                        {[10, 20, 50, 100].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>

                    <span>
                      Page {currentPage} of {totalPages} &nbsp;&mdash;&nbsp; {totalCount} records
                    </span>

                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={currentPage <= 1 || isFetching}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        &laquo; Prev
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={currentPage >= totalPages || isFetching}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Next &raquo;
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionsList;


